<main class="bg-black text-white min-h-screen overflow-x-hidden">
  <section class="relative min-h-[65vh] flex items-center pt-24 pb-14 overflow-hidden page-hero-gradient">
    <div class="absolute -top-48 right-0 w-[700px] h-[700px] bg-varroc-accent/5 rounded-full blur-3xl"></div>
    <div class="relative z-10 max-w-7xl mx-auto px-6 w-full">
      <div class="max-w-4xl">
        <a routerLink="/solutions/business" class="back-nav" data-aos="fade-down">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 svg-stroke-gradient"><polyline points="15 18 9 12 15 6"></polyline></svg>
          Back
        </a>
        <h1 class="hero-col font-black mb-5" data-aos="fade-up" data-aos-delay="100">
          FleetIQ <span class="text-varroc-accent">Fleet Management</span> Platform
        </h1>
        <p class="text-varroc-text-gray text-lg leading-relaxed mb-8 max-w-3xl" data-aos="fade-up" data-aos-delay="200">
          FleetIQ is a telematics-based fleet management web application that empowers fleet managers to monitor vehicle operations, driver behavior, maintenance, and compliance through one unified command center for owned, hired, and shared fleets.
        </p>
        <div class="flex flex-wrap gap-4" data-aos="fade-up" data-aos-delay="300">
          <a routerLink="/contact-us" class="bg-varroc-accent text-white font-bold px-8 py-4 rounded-md hover:bg-varroc-accent-hover hover:shadow-neon transition-all">Request a Demo</a>
          <a routerLink="/resources" class="border border-varroc-accent/40 font-semibold px-8 py-4 rounded-md hover:border-varroc-accent hover:bg-varroc-accent/10 transition-all">Explore Resources</a>
        </div>
      </div>
    </div>
  </section>
  <nav class="sticky-product-nav">
    <div class="max-w-7xl mx-auto px-6">
      <div class="flex overflow-x-auto">
        <a *ngFor="let s of sections" (click)="scrollTo(s.id)" [class.active]="activeSection === s.id" class="cursor-pointer flex-shrink-0">
          <i class='bx text-base' [class]="s.icon"></i> {{ s.label }}
        </a>
      </div>
    </div>
  </nav>
  <div class="max-w-7xl mx-auto px-6 py-20 space-y-28">
    <section id="overview" class="section-anchor">
      <div class="grid lg:grid-cols-[1.1fr,0.9fr] gap-10 items-start">
        <div data-aos="fade-right">
          <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Overview</span>
          <h2 class="text-4xl font-black mt-2 mb-4">A Unified Command Center for Fleet Operations</h2>
          <p class="text-varroc-text-gray text-lg leading-relaxed mb-5">FleetIQ centralizes real-time and historical vehicle data so businesses can optimize utilization, reduce operating costs, improve driver safety, automate billing, and stay compliant.</p>
          <p class="text-varroc-text-gray leading-relaxed">The platform supports both device-based and mobile telematics-based deployments, making it suitable for enterprise fleets, transport operators, field-force programs, employee transport, and last-mile delivery operations.</p>
        </div>
        <div class="grid sm:grid-cols-3 gap-5" data-aos="fade-left">
          <div *ngFor="let item of overviewHighlights" class="feature-card text-center">
            <div class="text-varroc-accent font-black text-2xl mb-2">{{ item.val }}</div>
            <div class="text-varroc-text-gray text-sm leading-relaxed">{{ item.label }}</div>
          </div>
        </div>
      </div>
      <!-- <div class="mt-12 flex justify-center" data-aos="fade-up">
        <img src="assets/images/websitev2/workflow-diagrams/transport-billing-system.png" alt="FleetIQ transport billing workflow diagram" class="w-full h-auto rounded-xl block">
      </div> -->
    </section>

    <div class="accent-divider"></div>

    <section id="problem" class="section-anchor">
      <div class="grid lg:grid-cols-[0.9fr,1.1fr] gap-10 items-start">
        <div data-aos="fade-right">
          <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Problem Statement</span>
          <h2 class="text-4xl font-black mt-2 mb-4">Why Fleet Operators Need FleetIQ</h2>
          <p class="text-varroc-text-gray text-lg leading-relaxed">Fleet operators often lack real-time visibility into vehicle status, driver behavior, and maintenance needs. This creates inefficiencies, higher costs, and compliance risks. Manual processes and fragmented systems make it difficult to act quickly or improve decision-making at scale.</p>
        </div>
        <div class="feature-card" data-aos="fade-left">
          <h3 class="text-white font-bold text-lg mb-4">What FleetIQ Solves</h3>
          <ul class="space-y-3">
            <li class="flex items-start gap-3 text-varroc-text-gray text-sm"><i class='bx bx-check-circle icon-gradient mt-0.5 flex-shrink-0'></i><span>Real-time visibility into fleet movement, utilization, and exceptions.</span></li>
            <li class="flex items-start gap-3 text-varroc-text-gray text-sm"><i class='bx bx-check-circle icon-gradient mt-0.5 flex-shrink-0'></i><span>Automated trip, billing, and compliance workflows that reduce manual effort.</span></li>
            <li class="flex items-start gap-3 text-varroc-text-gray text-sm"><i class='bx bx-check-circle icon-gradient mt-0.5 flex-shrink-0'></i><span>Unified driver, vehicle, and maintenance intelligence for faster decisions.</span></li>
            <li class="flex items-start gap-3 text-varroc-text-gray text-sm"><i class='bx bx-check-circle icon-gradient mt-0.5 flex-shrink-0'></i><span>Operational control across owned, third-party, and deviceless fleet models.</span></li>
          </ul>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>
    <section id="features" class="section-anchor">
      <div class="text-center mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Capabilities</span>
        <h2 class="text-4xl font-black mt-2 mb-4">FleetIQ Key Features</h2>
        <p class="text-varroc-text-gray max-w-3xl mx-auto">Built for visibility, compliance, billing, utilization, routing, reporting, and operational scale across modern fleet programs.</p>
      </div>
      <div class="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
        <div *ngFor="let f of keyFeatures; let i = index" class="feature-card" [attr.data-aos]="'fade-up'" [attr.data-aos-delay]="(i * 60).toString()">
          <div class="w-11 h-11 bg-varroc-accent/15 rounded-xl flex items-center justify-center mb-4">
            <i class='bx icon-gradient text-xl' [class]="f.icon"></i>
          </div>
          <h3 class="text-white font-bold mb-2">{{ f.title }}</h3>
          <p class="text-varroc-text-gray text-sm leading-relaxed">{{ f.desc }}</p>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <section id="usecases" class="section-anchor">
      <div class="text-center mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Use Cases</span>
        <h2 class="text-4xl font-black mt-2 mb-4">Where FleetIQ Creates Impact</h2>
        <p class="text-varroc-text-gray max-w-3xl mx-auto">FleetIQ supports billing, safety, attendance, tracking, enterprise integration, field force, and delivery operations through device-based and deviceless deployment models.</p>
      </div>
      <div class="grid md:grid-cols-2 gap-6">
        <div *ngFor="let item of useCases; let i = index" class="feature-card flex items-start gap-5" [attr.data-aos]="'fade-up'" [attr.data-aos-delay]="(i * 70).toString()">
          <div class="w-12 h-12 bg-varroc-accent/15 rounded-xl flex items-center justify-center flex-shrink-0">
            <i class='bx icon-gradient text-2xl' [class]="item.icon"></i>
          </div>
          <div>
            <h3 class="text-white font-bold text-lg mb-2">{{ item.title }}</h3>
            <p class="text-varroc-text-gray text-sm leading-relaxed">{{ item.desc }}</p>
          </div>
        </div>
      </div>
      <div class="mt-8 feature-card" data-aos="fade-up" data-aos-delay="120">
        <div class="flex items-start gap-5">
          <div class="w-12 h-12 bg-varroc-accent/15 rounded-xl flex items-center justify-center flex-shrink-0">
            <i class='bx bx-map-alt icon-gradient text-2xl'></i>
          </div>
          <div>
            <h3 class="text-white font-bold text-2xl mb-3">Parking Hub</h3>
            <p class="text-varroc-text-gray leading-relaxed">FleetIQ's Parking Hub helps you define and manage parking areas using simple geofencing, so you always know where your vehicles are parked. It shows how long a vehicle stays inside a parking hub, helping you identify underutilized or idle vehicles that are not being used efficiently. You can track entry and exit of vehicles, prevent unauthorized parking, and improve overall fleet usage. This feature is especially useful for managing factory yards, warehouses, and corporate parking spaces. It is also highly effective for rental car fleet hubs, where tracking vehicle availability and idle time is critical for maximizing revenue.</p>
          </div>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <section id="all-features" class="section-anchor">
      <div class="text-center mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Full Platform Coverage</span>
        <h2 class="text-4xl font-black mt-2 mb-4">All Features Across Operations, Alerts, Reports, and Admin</h2>
        <p class="text-varroc-text-gray max-w-3xl mx-auto">FleetIQ covers operational visibility, alerting, transport billing, compliance reporting, vehicle and driver administration, group controls, and expert support in one platform.</p>
      </div>
      <div class="grid xl:grid-cols-2 gap-6">
        <div *ngFor="let group of featureGroups; let i = index" class="feature-card" [attr.data-aos]="'fade-up'" [attr.data-aos-delay]="(i * 60).toString()">
          <div class="flex items-center gap-3 mb-5">
            <div class="w-12 h-12 bg-varroc-accent/15 rounded-xl flex items-center justify-center flex-shrink-0">
              <i class='bx icon-gradient text-2xl' [class]="group.icon"></i>
            </div>
            <div>
              <h3 class="text-white font-bold text-xl">{{ group.title }}</h3>
            </div>
          </div>
          <div class="grid gap-4 sm:grid-cols-2">
            <div *ngFor="let item of group.items" class="rounded-xl border border-white/10 bg-white/5 p-4">
              <div class="text-white font-semibold text-sm mb-1">{{ item.name }}</div>
              <div class="text-varroc-text-gray text-xs leading-relaxed">{{ item.desc }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

  </div>
</main>
